import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import Modal from './Modal';
import { fetchUpgradePreview, upgradeToPro } from '../../Shared/Api';
import { EXTRA_SEAT_MONTHLY, PLAN_PRICE, PRO_INCLUDED_SEATS } from '../../Shared/pricing';

const UpgradePlanConfirmModal = ({
	open,
	onClose,
	onSuccess,
	enqueueSnackbar,
	prepaidCommitActive = false,
}) => {
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [preview, setPreview] = useState(null);

	useEffect(() => {
		if (!open) {
			return;
		}
		setLoading(true);
		fetchUpgradePreview()
			.then((response) => {
				if (response?.requires_seat_selection) {
					enqueueSnackbar?.(
						'Choose which accounts to restore before upgrading.',
						{ variant: 'warning' },
					);
					onClose?.();
					return;
				}
				setPreview(response);
			})
			.catch((error) => {
				enqueueSnackbar?.(error.message || 'Failed to load upgrade preview.', {
					variant: 'error',
				});
				onClose?.();
			})
			.finally(() => setLoading(false));
	}, [open, enqueueSnackbar, onClose]);

	const costPreview = preview?.cost_preview;
	const showPrepaidForfeit = Boolean(
		preview?.prepaid_commit_active ?? prepaidCommitActive,
	);
	const proBase =
		costPreview?.pro_base_monthly_usd ?? PLAN_PRICE.pro;
	const extraSeat = costPreview?.extra_seat_monthly_usd ?? EXTRA_SEAT_MONTHLY;
	const includedSeats = costPreview?.included_seats ?? PRO_INCLUDED_SEATS;

	const handleConfirm = async () => {
		setSubmitting(true);
		try {
			await upgradeToPro();
			enqueueSnackbar?.('Upgraded to Pro. Your plan is active now.', {
				variant: 'success',
			});
			onSuccess?.();
			onClose?.();
		} catch (error) {
			enqueueSnackbar?.(error.message || 'Upgrade failed.', {
				variant: 'error',
			});
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Modal
			open={open}
			onClose={onClose}
			modalTitle="Upgrade to Pro"
			maxWidth="sm"
			useActionButtons
			actionButtons={
				<>
					<Button onClick={onClose} disabled={submitting}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="primary"
						onClick={handleConfirm}
						disabled={loading || submitting}
					>
						{submitting ? 'Upgrading...' : 'Confirm upgrade'}
					</Button>
				</>
			}
		>
			{loading ? (
				<Typography variant="body2" color="text.secondary">
					Loading upgrade preview...
				</Typography>
			) : (
				<Box sx={{ mt: 1 }}>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						Pro is ${proBase}/mo and includes up to {includedSeats} active seats. Extra seats are
						${extraSeat}/mo each. Upgrades take effect immediately with prorated billing.
					</Typography>

					{costPreview ? (
						<Box sx={{ mb: 2 }}>
							<Typography variant="subtitle2" gutterBottom>
								Cost summary
							</Typography>
							<Typography variant="body2" color="text.secondary">
								${proBase}/mo base
								{costPreview.target_billable_extra_seats > 0
									? ` + ${costPreview.target_billable_extra_seats} extra seat(s) at $${extraSeat}/mo`
									: ''}
							</Typography>
							{costPreview.prorated_amount_cents != null ? (
								<Typography variant="body2" color="text.secondary">
									{costPreview.prorated_credit
										? 'Prorated credit'
										: 'Due today'}
									: $
									{(Math.abs(costPreview.prorated_amount_cents) / 100).toFixed(
										2,
									)}
								</Typography>
							) : null}
						</Box>
					) : null}

					{showPrepaidForfeit ? (
						<Alert severity="warning" sx={{ mb: 1.5 }}>
							You are on the 3-month prepaid term. Upgrading converts your
							subscription to monthly Pro immediately and forfeits any remaining
							prepaid time.
						</Alert>
					) : null}

					<Alert severity="info">
						You can add team members from the Users tab after upgrading.
					</Alert>
				</Box>
			)}
		</Modal>
	);
};

export default UpgradePlanConfirmModal;
